import { z } from "zod";
import { ApiError } from "../../../api/api-client";
import { responseNow, responseSecondsRemaining } from "../../../api/response-time";
import { tierName } from "../../../components/TierPortrait";

const preparedDetails = z.object({
  selectedTierUsd: z.number().int().positive(),
  deadline: z.number().int().positive().max(8_640_000_000_000),
});

export function preparedMintError(error: unknown): ApiError | null {
  return error instanceof ApiError && error.code === "PROOF_AUTHORIZATION_ALREADY_PREPARED" ? error : null;
}

export function mintActionErrorCode(error: unknown): string | null {
  return error instanceof ApiError && error.code === "LOCAL_EVM_MINT_SIMULATION_FAILED" ? error.code : null;
}

const reasonDetails = z.object({ blockingReasons: z.array(z.string()) });
const financialMessages: Record<string, string> = {
  PREPARE_FINANCIAL_SYNC_TIMEOUT: "The financial wait timed out; the scanner continues. You can retry explicitly.",
  PREPARE_FINANCIAL_SYNC_FAILED: "The scan failed and no authorization was issued. Retry later.",
  PREPARE_FINANCIAL_SCANNER_STOPPED: "The financial scanner is stopping. No wallet request was made.",
  PREPARE_FINANCIAL_SNAPSHOT_UNAVAILABLE: "No valid committed financial snapshot is available. Retry after refreshing.",
};
export function mintActionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (mintActionErrorCode(error)) {
      return "Unable to prepare the mint transaction. Please check your balance and mint eligibility, then try again.";
    }
    if (preparedMintError(error)) {
      const parsed = preparedDetails.safeParse(error.details);
      if (!parsed.success) return "This proof already has a mint authorization for another tier. The previous tier and expiry are unavailable. Refresh mint conditions and try again.";
      const { selectedTierUsd, deadline } = parsed.data;
      const clock = error.details as object;
      const identity = `${tierName(selectedTierUsd)} (eligibility tier $${selectedTierUsd.toLocaleString("en-US")})`;
      const expiry = new Date(deadline * 1_000).toLocaleString();
      const prefix = `An existing mint authorization was signed for ${identity}. This does not confirm that an NFT was minted.`;
      if (responseNow(clock) === null) return `${prefix} Quote expires at ${expiry}. Countdown unavailable; refresh mint conditions to check expiry.`;
      const remaining = responseSecondsRemaining(clock, deadline);
      return remaining > 0
        ? `${prefix} Quote expires at ${expiry} (${remaining}s remaining). Select the previous tier to continue, or wait before preparing another tier.`
        : `${prefix} The previous quote has expired. You can check and prepare mint again for your selected tier.`;
    }
    const details = reasonDetails.safeParse(error.details);
    return `${error.code}: ${error.message}${details.success && details.data.blockingReasons.length ?
      ` (${details.data.blockingReasons.join(", ")})` : ""} ${financialMessages[error.code] ?? "Refresh conditions and retry explicitly."}`;
  }
  return error instanceof Error && error.message.trim() ? error.message : "Mint could not be completed.";
}
